import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Instagram, Facebook, Youtube } from 'lucide-react';
import { siteInfo } from '../data/siteData';
import { Button } from './ui/button';

const navLinks = [
  { id: 'home', name: "Home", path: "/" },
  { 
    id: 'elezioni-2025',
    name: "Elezioni 2025", 
    path: "#",
    submenu: [
      { id: 'comunali', name: "Comunali Aosta", path: "/elezioni-comunali" },
      { id: 'regionali', name: "Regionali VdA", path: "/elezioni-regionali" }
    ]
  },
  { id: 'programma', name: "Programma", path: "/programma-regionale" },
  { 
    id: 'chi-siamo',
    name: "Chi Siamo", 
    path: "#",
    submenu: [
      { id: 'storia', name: "La Storia", path: "/storia" },
      { id: 'valori', name: "I Nostri Valori", path: "/valori" }
    ]
  },
  { id: 'trasparenza', name: "Trasparenza", path: "/elezioni-trasparenti" }
];

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const location = useLocation();

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 20);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setOpenSubmenu(null);
  }, [location.pathname]);

  const toggleSubmenu = (index) => {
    setOpenSubmenu(openSubmenu === index ? null : index);
  };

  return (
    <header 
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'glass shadow-lg' 
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-3"
            data-testid="header-logo"
          >
            <img 
              src={siteInfo.logo} 
              alt={siteInfo.name}
              className="h-14 w-14 rounded-full object-cover shadow-md"
            />
            <span className={`hidden md:block font-serif font-bold text-lg transition-colors ${
              isScrolled ? 'text-[#002060]' : 'text-white'
            }`}>
              La Renaissance
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <div key={link.id} className="relative group">
                {link.submenu ? (
                  <button
                    className={`flex items-center gap-1 px-4 py-2 font-medium text-sm transition-colors rounded-lg ${
                      isScrolled 
                        ? 'text-[#002060] hover:bg-[#002060]/5' 
                        : 'text-white hover:bg-white/10'
                    }`}
                    onClick={() => toggleSubmenu(link.id)}
                    data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                    <ChevronDown className="w-4 h-4" />
                  </button>
                ) : (
                  <Link
                    to={link.path}
                    className={`px-4 py-2 font-medium text-sm transition-colors rounded-lg ${
                      isScrolled 
                        ? 'text-[#002060] hover:bg-[#002060]/5' 
                        : 'text-white hover:bg-white/10'
                    } ${location.pathname === link.path ? 'bg-[#5CB9E5]/20' : ''}`}
                    data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                )}
                
                {/* Dropdown */}
                {link.submenu && (
                  <div className="absolute top-full left-0 mt-1 py-2 min-w-[200px] bg-white rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 border border-slate-100">
                    {link.submenu.map((sublink) => (
                      <Link
                        key={sublink.id}
                        to={sublink.path}
                        className="block px-4 py-2 text-sm text-[#002060] hover:bg-[#5CB9E5]/10 transition-colors"
                        data-testid={`nav-sublink-${sublink.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {sublink.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Desktop CTA and Social */}
          <div className="hidden lg:flex items-center gap-4">
            <div className="flex items-center gap-2">
              <a 
                href={siteInfo.social.facebook} 
                target="_blank" 
                rel="noopener noreferrer"
                className={`p-2 rounded-full transition-colors ${
                  isScrolled ? 'text-[#002060] hover:bg-[#002060]/5' : 'text-white hover:bg-white/10'
                }`}
                data-testid="social-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a 
                href={siteInfo.social.instagram} 
                target="_blank" 
                rel="noopener noreferrer"
                className={`p-2 rounded-full transition-colors ${
                  isScrolled ? 'text-[#002060] hover:bg-[#002060]/5' : 'text-white hover:bg-white/10'
                }`}
                data-testid="social-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href={siteInfo.social.youtube} 
                target="_blank" 
                rel="noopener noreferrer"
                className={`p-2 rounded-full transition-colors ${
                  isScrolled ? 'text-[#002060] hover:bg-[#002060]/5' : 'text-white hover:bg-white/10'
                }`}
                data-testid="social-youtube"
              >
                <Youtube className="w-5 h-5" />
              </a>
            </div>
            <Link to="/unisciti">
              <Button 
                className="btn-gold px-6 py-2 rounded-lg font-semibold text-sm"
                data-testid="cta-join-button"
              >
                Unisciti a noi
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`lg:hidden p-2 rounded-lg transition-colors ${
              isScrolled ? 'text-[#002060]' : 'text-white'
            }`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden glass border-t border-slate-200"
          data-testid="mobile-menu"
        >
          <div className="max-w-7xl mx-auto px-4 py-4">
            <nav className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <div key={link.id}>
                  {link.submenu ? (
                    <>
                      <button
                        className="flex items-center justify-between w-full px-4 py-3 text-[#002060] font-medium rounded-lg hover:bg-[#002060]/5"
                        onClick={() => toggleSubmenu(link.id)}
                      >
                        {link.name}
                        <ChevronDown className={`w-4 h-4 transition-transform ${openSubmenu === link.id ? 'rotate-180' : ''}`} />
                      </button>
                      {openSubmenu === link.id && (
                        <div className="ml-4 mt-1 space-y-1">
                          {link.submenu.map((sublink) => (
                            <Link
                              key={sublink.id}
                              to={sublink.path}
                              className="block px-4 py-2 text-sm text-[#002060]/80 hover:text-[#002060] hover:bg-[#5CB9E5]/10 rounded-lg"
                            >
                              {sublink.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={link.path}
                      className={`block px-4 py-3 text-[#002060] font-medium rounded-lg hover:bg-[#002060]/5 ${
                        location.pathname === link.path ? 'bg-[#5CB9E5]/20' : ''
                      }`}
                    >
                      {link.name}
                    </Link>
                  )}
                </div>
              ))}
            </nav>
            
            <div className="mt-4 pt-4 border-t border-slate-200">
              <div className="flex items-center justify-center gap-4 mb-4">
                <a 
                  href={siteInfo.social.facebook} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="p-2 text-[#002060] hover:text-[#5CB9E5]"
                >
                  <Facebook className="w-6 h-6" />
                </a>
                <a 
                  href={siteInfo.social.instagram} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="p-2 text-[#002060] hover:text-[#5CB9E5]"
                >
                  <Instagram className="w-6 h-6" />
                </a>
                <a 
                  href={siteInfo.social.youtube} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="p-2 text-[#002060] hover:text-[#5CB9E5]"
                >
                  <Youtube className="w-6 h-6" />
                </a>
              </div>
              <Link to="/unisciti" className="block">
                <Button className="w-full btn-gold py-3 rounded-lg font-semibold">
                  Unisciti a noi
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
